/**
 * useGifSearch — debounced Giphy search state shared by the mobile and web
 * GIF pickers. UI stays platform-specific; this owns fetching only.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import giphyService from '../api/services/giphyService';
import { GIPHY_SEARCH_DEBOUNCE_MS } from '../constants/giphy';
import type { GiphyGif } from '../types/gif';

export interface UseGifSearchResult {
  search: string;
  setSearch: (value: string) => void;
  gifs: GiphyGif[];
  loading: boolean;
  error: boolean;
  /** False when no Giphy API key is configured for the current build. */
  configured: boolean;
  retry: () => void;
}

export const useGifSearch = (): UseGifSearchResult => {
  const configured = giphyService.isConfigured();
  const [search, setSearch] = useState('');
  const [gifs, setGifs] = useState<GiphyGif[]>([]);
  // Start true so the first open never flashes an empty panel before the fetch begins.
  const [loading, setLoading] = useState(configured);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const hasLoadedOnceRef = useRef(false);

  useEffect(() => {
    if (!configured) {
      setGifs([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : undefined;

    const run = async () => {
      setLoading(true);
      setError(false);
      try {
        const results = await giphyService.fetchGifs(search, controller?.signal);
        if (cancelled) return;
        setGifs(results);
      } catch (err) {
        if (cancelled) return;
        console.error('[useGifSearch] Failed to fetch GIFs:', err);
        setGifs([]);
        setError(true);
      } finally {
        if (!cancelled) {
          setLoading(false);
          hasLoadedOnceRef.current = true;
        }
      }
    };

    // Skip the debounce on the first load so the spinner shows immediately.
    const timer = setTimeout(
      run,
      hasLoadedOnceRef.current ? GIPHY_SEARCH_DEBOUNCE_MS : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller?.abort();
    };
  }, [search, configured, retryToken]);

  const retry = useCallback(() => setRetryToken(token => token + 1), []);

  return { search, setSearch, gifs, loading, error, configured, retry };
};

export default useGifSearch;
